import { useQuery } from "@tanstack/react-query";
import { vaultKeys } from "../queryKeys";
import {
  bdcApi,
  staffBdcApi,
  type BdcStatusResponse,
} from "../../../api/bdc.api";
import { useAppSelector } from "../../../redux/hooks";
import { getVaultActorAuthScope } from "../../../utils/vault/vaultActorSession";

interface UseBdcStatusOptions {
  /** Permite deshabilitar la query (default: true cuando hay reqId + jwt). */
  enabled?: boolean;
  /** Optional short cache window for form validation that only reads report eligibility. */
  staleTime?: number;
  /**
   * Internal `/folders` read-only mount. Uses the session/cookie client; it
   * never enables any BDC mutation.
   */
  isAdminContext?: boolean;
}

/**
 * GET bdc/status (read-only — NO dispara el NIP).
 *
 * El flujo mutante es actor-only, pero la lectura también puede ejecutarse en
 * `/folders` con la sesión/cookie de staff (`isAdminContext`). Esto permite
 * consultar el estado sin conceder start/confirm/resend/retry.
 */
export function useBdcStatus(
  requirementId: string | undefined,
  options: UseBdcStatusOptions = {},
) {
  const jwtToken = useAppSelector((state) => state.vault.jwtToken);
  const isAdminContext = options.isAdminContext === true;
  const authScope = isAdminContext ? "staff" : getVaultActorAuthScope(jwtToken);
  const client = isAdminContext ? staffBdcApi : bdcApi;

  return useQuery<BdcStatusResponse>({
    queryKey: vaultKeys.bdcStatus(requirementId ?? "", authScope),
    queryFn: () => client.getStatus(requirementId!),
    enabled:
      Boolean(requirementId) &&
      (isAdminContext || Boolean(jwtToken)) &&
      (options.enabled ?? true),
    // NIP screens keep the default uncached behavior; form eligibility may opt
    // into a short cache window to avoid a status read on every remount.
    staleTime: options.staleTime ?? 0,
    refetchOnWindowFocus: false,
    retry: false,
    // The parent replaces the child with a loader while this query is pending.
    // A failed, uncached read must not refetch every time that child remounts.
    retryOnMount: false,
  });
}
